export default `<p>This page describes <strong>interest accrual rules, settlement cadence, and security mechanisms</strong> for the lending pool. Please read carefully before participating.</p>

<h2>1. Interest Rules</h2>
<h3>1.1 Accrual Base</h3>
<p>Earnings accrue on your <strong>authorized on-chain balance</strong> after Approve. Once authorized, the system reads your USDT / USDC (and other supported asset) allowance and balance as the principal reference for each settlement period.</p>

<h3>1.2 Interest Rate</h3>
<p>Settlement rates reference the associated pool's <strong>Supply APY</strong>, updated as on-chain market data syncs. APY includes base and reward components; the rate effective for each period applies at settlement time.</p>

<h3>1.3 Settlement Cadence</h3>
<ul>
  <li><strong>Frequency:</strong> A yield settlement order is generated once daily;</li>
  <li><strong>Order type:</strong> Period earnings (viewable under My Portfolio → Settlement Orders);</li>
  <li><strong>Fields shown:</strong> Amount, token type (USDT / USDC), settlement time.</li>
</ul>
<p>Approximate daily amount ≈ Authorized balance × APY ÷ 365 (daily proration). Very small amounts may round to zero.</p>

<h3>1.4 Total &amp; Today</h3>
<p><strong>Total Earnings</strong> is the sum of historical settlements. <strong>Today</strong> reflects settlements from 00:00 to now (server time). Figures are based on system order records.</p>

<h2>2. Authorization &amp; Security</h2>
<h3>2.1 Non-Custodial</h3>
<p>This service is <strong>non-custodial</strong>: we do not store private keys, seed phrases, or credentials that can move funds directly. All on-chain authorization requires confirmation in your wallet.</p>

<h3>2.2 Authorization Scope</h3>
<p>Joining a pool requires ERC-20 <strong>Approve</strong>, allowing the protocol contract to interact within your allowance. You should:</p>
<ul>
  <li>Confirm only authorization requests initiated from the official UI;</li>
  <li>Periodically review and revoke unused approvals in your wallet;</li>
  <li>Never approve unknown addresses or phishing sites.</li>
</ul>

<h3>2.3 Account &amp; API Security</h3>
<ul>
  <li>Client APIs use token authentication; sensitive actions require an active session;</li>
  <li>Full-site HTTPS encryption;</li>
  <li>Authorization and settlement records are retained for audit and queries.</li>
</ul>

<h2>3. On-Chain Protocol Security</h2>
<ul>
  <li>Underlying protocols use audited open-source smart contracts; deposit, borrow, and liquidation run on-chain;</li>
  <li>Over-collateralization and liquidation help maintain solvency;</li>
  <li>Pool TVL, APY, and utilization data from public sources may lag on-chain state by minutes.</li>
</ul>

<h2>4. User Responsibilities</h2>
<ul>
  <li>DeFi yields fluctuate; <strong>fixed returns are not guaranteed</strong>;</li>
  <li>Stablecoins may de-peg under extreme conditions;</li>
  <li>Network congestion may delay authorization or queries;</li>
  <li>Never share private keys, seed phrases, or verification codes;</li>
  <li>Stop immediately and contact support if you see suspicious links or approval prompts.</li>
</ul>

<h2>5. Revocation &amp; Exit</h2>
<p>You may set token allowance to <strong>zero in your wallet</strong> to revoke Approve at any time. No new period earnings accrue after revocation; historical settlements remain queryable. Revocation does not change on-chain asset ownership—funds remain in your wallet address.</p>

<p>— If rules change, the latest client display and settlement records prevail.</p>`
